import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Loader2, Save, User, MapPin, Briefcase, DollarSign, CheckCircle } from "lucide-react";
import { getMyProfile, updateMyProfile, CustomerProfile } from "@/Calls/callCustomer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const US_STATES = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
    "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
    "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
            {children}
        </div>
    );
}

function SectionHeader({ icon: Icon, title }: { icon: React.ElementType; title: string }) {
    return (
        <div className="flex items-center gap-2 pb-2 border-b">
            <div className="rounded-full bg-indigo-100 p-1.5">
                <Icon className="h-4 w-4 text-indigo-600" />
            </div>
            <h2 className="text-sm font-semibold uppercase tracking-wide">{title}</h2>
        </div>
    );
}

export default function CustomerProfilePage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { register, handleSubmit, setValue, watch, reset } = useForm<CustomerProfile>();

    useEffect(() => {
        getMyProfile()
            .then((p) => reset(p))
            .catch(() => setError("Failed to load profile"))
            .finally(() => setLoading(false));
    }, [reset]);

    async function onSubmit(data: CustomerProfile) {
        setSaving(true);
        setError(null);
        try {
            await updateMyProfile(data);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch {
            setError("Failed to save profile. Please try again.");
        } finally {
            setSaving(false);
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="max-w-3xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-light uppercase tracking-wide">My Profile</h1>
                    <p className="text-muted-foreground text-sm mt-1">Keep your information up to date for faster deal processing.</p>
                </div>
                <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : saved ? <CheckCircle className="h-4 w-4 mr-2 text-emerald-300" /> : <Save className="h-4 w-4 mr-2" />}
                    {saved ? "Saved!" : "Save Profile"}
                </Button>
            </div>

            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            {/* Personal Info */}
            <Card className="p-5 space-y-4">
                <SectionHeader icon={User} title="Personal Information" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="First Name">
                        <Input {...register("customer_fname")} placeholder="First" />
                    </Field>
                    <Field label="Middle Name">
                        <Input {...register("customer_mname")} placeholder="Middle" />
                    </Field>
                    <Field label="Last Name">
                        <Input {...register("customer_lname")} placeholder="Last" />
                    </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Suffix">
                        <Input {...register("customer_suffix")} placeholder="Jr., Sr., III" />
                    </Field>
                    <Field label="Date of Birth">
                        <Input {...register("customer_dob")} placeholder="MM/DD/YYYY" />
                    </Field>
                    <Field label="Cell Phone">
                        <Input {...register("customer_cellphone")} placeholder="(555) 555-5555" />
                    </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Home Phone">
                        <Input {...register("customer_phoneno")} placeholder="(555) 555-5555" />
                    </Field>
                    <Field label="Email Address">
                        <Input {...register("customer_email")} type="email" placeholder="you@example.com" />
                    </Field>
                </div>
            </Card>

            {/* Driver's License */}
            <Card className="p-5 space-y-4">
                <SectionHeader icon={User} title="Driver's License" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="DL Number">
                        <Input {...register("customer_dlno")} placeholder="DL12345678" />
                    </Field>
                    <Field label="State Issued">
                        <Select onValueChange={(v) => setValue("customer_dlstate", v)} value={watch("customer_dlstate") ?? ""}>
                            <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                            <SelectContent>
                                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Expiration Date">
                        <Input {...register("customer_dlexpdate")} placeholder="MM/DD/YYYY" />
                    </Field>
                </div>
            </Card>

            {/* Home Address */}
            <Card className="p-5 space-y-4">
                <SectionHeader icon={MapPin} title="Home Address" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Address Line 1">
                        <Input {...register("customer_home_addr1")} placeholder="123 Main St" />
                    </Field>
                    <Field label="Address Line 2">
                        <Input {...register("customer_home_addr2")} placeholder="Apt, Suite, etc." />
                    </Field>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="col-span-2">
                        <Field label="City">
                            <Input {...register("customer_home_city")} placeholder="City" />
                        </Field>
                    </div>
                    <Field label="State">
                        <Select onValueChange={(v) => setValue("customer_home_state", v)} value={watch("customer_home_state") ?? ""}>
                            <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                            <SelectContent>
                                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="ZIP">
                        <Input {...register("customer_home_zip")} placeholder="12345" />
                    </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="County">
                        <Input {...register("customer_home_county")} placeholder="County" />
                    </Field>
                    <Field label="Years at Address">
                        <Input {...register("customer_home_live_years")} placeholder="0" type="number" min="0" />
                    </Field>
                    <Field label="Months at Address">
                        <Input {...register("customer_home_live_months")} placeholder="0" type="number" min="0" max="11" />
                    </Field>
                </div>
            </Card>

            {/* Employment */}
            <Card className="p-5 space-y-4">
                <SectionHeader icon={Briefcase} title="Employment" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Employer Name">
                        <Input {...register("customer_employer_name")} placeholder="Company Name" />
                    </Field>
                    <Field label="Employer Phone">
                        <Input {...register("customer_employer_phone")} placeholder="(555) 555-5555" />
                    </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Employer Address">
                        <Input {...register("customer_employer_addr1")} placeholder="123 Business Ave" />
                    </Field>
                    <Field label="City">
                        <Input {...register("customer_employer_city")} placeholder="City" />
                    </Field>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <Field label="State">
                        <Select onValueChange={(v) => setValue("customer_employer_state", v)} value={watch("customer_employer_state") ?? ""}>
                            <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                            <SelectContent>
                                {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="ZIP">
                        <Input {...register("customer_employer_zip")} placeholder="12345" />
                    </Field>
                    <Field label="Years">
                        <Input {...register("customer_employer_years")} placeholder="0" type="number" min="0" />
                    </Field>
                    <Field label="Months">
                        <Input {...register("customer_employer_months")} placeholder="0" type="number" min="0" max="11" />
                    </Field>
                </div>
                <Field label="Hire Date">
                    <Input {...register("customer_employer_hiredate")} placeholder="MM/DD/YYYY" className="sm:w-1/3" />
                </Field>
            </Card>

            {/* Income */}
            <Card className="p-5 space-y-4">
                <SectionHeader icon={DollarSign} title="Income" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Gross Monthly Income">
                        <Input {...register("customer_gross_income")} placeholder="0.00" type="number" step="0.01" min="0" />
                    </Field>
                    <Field label="Net Monthly Income">
                        <Input {...register("customer_net_income")} placeholder="0.00" type="number" step="0.01" min="0" />
                    </Field>
                    <Field label="Pay Frequency">
                        <Select onValueChange={(v) => setValue("customer_income_frequency", v)} value={watch("customer_income_frequency") ?? ""}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="WEEKLY">Weekly</SelectItem>
                                <SelectItem value="BIWEEKLY">Bi-Weekly</SelectItem>
                                <SelectItem value="SEMIMONTHLY">Semi-Monthly</SelectItem>
                                <SelectItem value="MONTHLY">Monthly</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                </div>
            </Card>

            <div className="flex justify-end pb-6">
                <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white px-8">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Profile
                </Button>
            </div>
        </form>
    );
}
